import { useParams } from 'react-router-dom';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { Drawer, SelectChangeEvent, Tooltip, Typography } from '@mui/material';

import { Button } from '@atoms';
import { DefaultFilter, Table } from '@molecules';
import { FeedbackItem } from 'src/features/Enterprise/SentimentAnalysis/types';
import { useSentimentAnalysis } from 'src/features/Enterprise/SentimentAnalysis/useSentimentAnalysis';
import {
  feedbackCategoryOptions,
  feedbackSubCategoryOptions,
  sentimentOptions,
} from 'src/features/Enterprise/SentimentAnalysis/utils';

import TopReportedProblemsModal from '../../../features/Enterprise/SentimentAnalysis/TopReportedProblemsModal';
import { getSentimentAnalysisColumns } from '../../../features/Enterprise/SentimentAnalysis/sentimentAnalysisTable.config';
import { AddSentimentAnalysisModal } from 'src/features/Enterprise/SentimentAnalysis/AddSentimentAnalysisModal/AddSentimentAnalysisModal';
import QueryKeys from '@constants/query-keys';
import { useModalState } from '@hooks';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { ReactNode } from 'react';
import { FilterItem } from '@services';
import { ConfirmModal } from '@organisms';
import { DeleteModalIcon } from '@atoms/Icons/DeleteModalIcon';

const FILTERS_LIST_SENTIMENT_ANALYSIS: FilterItem[] = [
  {
    name: 'Category',
    key: 'category',
    getFilter: (_onChange, onClose, setFilters, filter): ReactNode => (
      <DefaultFilter
        key="category"
        name="Category"
        width="initial"
        value={filter.category}
        items={feedbackCategoryOptions}
        onChange={(event: SelectChangeEvent<unknown>) =>
          setFilters({ ...filter, category: event.target.value as string })
        }
        onClose={onClose}
      />
    ),
  },
  {
    name: 'SubCategory',
    key: 'subCategory',
    getFilter: (_onChange, onClose, setFilters, filter): ReactNode => (
      <DefaultFilter
        key="subCategory"
        name="SubCategory"
        width="initial"
        value={filter.subCategory}
        items={feedbackSubCategoryOptions}
        onChange={(event: SelectChangeEvent<unknown>) =>
          setFilters({ ...filter, subCategory: event.target.value as string })
        }
        onClose={onClose}
      />
    ),
  },
  {
    name: 'Sentiment',
    key: 'sentiment',
    getFilter: (_onChange, onClose, setFilters, filter): ReactNode => (
      <DefaultFilter
        key="sentiment"
        name="Sentiment"
        width="initial"
        value={filter.sentiment}
        items={sentimentOptions}
        onChange={(event: SelectChangeEvent<unknown>) =>
          setFilters({ ...filter, sentiment: event.target.value as string })
        }
        onClose={onClose}
      />
    ),
  },
];

const EnterpriseSentimentAnalysis = () => {
  const { id: organizationId } = useParams<{ id: string }>();

  const [isOpenDrawer, , openDrawer, closeDrawer] = useModalState(false);
  const [isConfirmDelete, , openConfirmDeleteModal, closeConfirmDeleteModal] = useModalState(false);

  const {
    isOpenModal,
    defaultItem,
    editingItem,
    openModal,
    closeModal,
    setEditingItem,
    onEdit,
    onDelete,
  } = useSentimentAnalysis();

  const onClickEdit = (item: FeedbackItem) => {
    onEdit(item);
    openModal();
  };

  const onClickDelete = (item: FeedbackItem) => {
    setEditingItem(item);
    openConfirmDeleteModal();
  };

  const onCloseConfirmationDeleteModal = () => {
    setEditingItem(defaultItem);
    closeConfirmDeleteModal();
  };

  const onSubmitDelete = () => {
    onDelete(editingItem);
    closeConfirmDeleteModal();
  };

  const onCloseEditModal = () => {
    setEditingItem(defaultItem);
    closeModal();
  };

  return (
    <>
      <Table<FeedbackItem>
        searchPlaceholder="Search for News"
        columns={getSentimentAnalysisColumns({ onClickEdit, onClickDelete })}
        queryName={QueryKeys.enterpriseSentimentAnalysis}
        apiClient={ApiClientEnterprise}
        apiRoute={`/enterprises-admin/${organizationId}/sentiment-analysis`}
        filtersList={FILTERS_LIST_SENTIMENT_ANALYSIS}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        pinnedColumns={{
          left: ['__check__', 'title'],
          right: ['action'],
        }}
        renderAdditionalControl={
          <Tooltip title="Top Reported Problems" placement="top">
            <Button variant="contained" onClick={openDrawer}>
              <SummarizeIcon />
            </Button>
          </Tooltip>
        }
        usingIsFetching
        customSxForTable={{
          '& .MuiDataGrid-columnSeparator': {
            display: 'contents !important',
            '& svg': {
              pointerEvents: 'initial',
            },
          },
        }}
      />
      <Drawer
        PaperProps={{
          sx: { width: '900px', height: '100%' },
        }}
        open={isOpenDrawer}
        onClose={closeDrawer}
        anchor="right"
      >
        <TopReportedProblemsModal />
      </Drawer>
      <AddSentimentAnalysisModal
        open={isOpenModal}
        onClose={onCloseEditModal}
        title="Edit Analysis"
        subtitle="Here you can edit Analysis"
        categories={feedbackCategoryOptions}
        subCategories={feedbackSubCategoryOptions}
        editingItem={editingItem}
      />
      <ConfirmModal
        open={isConfirmDelete}
        onCancel={onCloseConfirmationDeleteModal}
        onSubmit={onSubmitDelete}
        icon={<DeleteModalIcon width={55} height={71} />}
        title={
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F6080',
              whiteSpace: 'normal',
              marginBottom: '-8px',
            }}
          >
            Are you sure you want to delete this item?
          </Typography>
        }
        submitText="Delete"
        submitColor="error"
        usingModalV2
      />
    </>
  );
};

export default EnterpriseSentimentAnalysis;
